import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, XCircle, Plus, Minus, HelpCircle, MessageCircle, Mail } from 'lucide-react';
import { services } from '../data/services';

export const ServicePage = () => {
  const { slug } = useParams();
  const data = services[slug];
  const [openFaq, setOpenFaq] = useState(0);

  if (!data) return <Navigate to="/services" replace />;

  return (
    <div data-testid={`service-page-${slug}`} className="w-full">
      {/* Hero */}
      <section data-testid="service-hero" className="relative bg-[#0F2A4A] text-white px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute top-20 -left-32 w-96 h-96 bg-[#3FC9E8]/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute bottom-20 -right-32 w-96 h-96 bg-[#1A9FE0]/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(#3FC9E8 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} />

        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <Link to="/services" className="text-xs uppercase tracking-[0.2em] text-[#3FC9E8] hover:text-white transition-colors" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Services</Link>
              <span className="text-white/40">/</span>
              <span className="text-xs uppercase tracking-[0.2em] text-white/60" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.title}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {data.hero.headline} <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{data.hero.headlineAccent}</span>
            </h1>
            <p className="text-lg lg:text-xl text-white/80 leading-relaxed mb-8 max-w-3xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.hero.subheadline}</p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link to="/contact" data-testid="hero-cta-primary" className="inline-flex items-center justify-center bg-[#3FC9E8] text-[#0F2A4A] font-medium px-8 py-4 hover:bg-[#1A9FE0] hover:text-white transition-all duration-300 active:scale-95">
                Book a FREE Strategy Call <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link to="/contact?scorecard=true" data-testid="hero-cta-secondary" className="inline-flex items-center justify-center border-2 border-white/30 text-white bg-transparent hover:bg-white hover:text-[#0F2A4A] font-medium px-8 py-4 transition-all duration-300 active:scale-95">
                Get The Brand Clarity Scorecard
              </Link>
            </div>
            <p className="text-sm text-[#3FC9E8] italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.tagline}</p>
          </motion.div>
        </div>
      </section>

      {/* The Problem */}
      <section className="relative bg-white px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#0F2A4A 1px, transparent 1px), linear-gradient(90deg, #0F2A4A 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.problem.label}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.problem.headline}</h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-lg text-[#5A6B7E] leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.problem.intro}</p>
            </div>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4 lg:gap-6">
            {data.problem.bullets.map((bullet, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.08 }} viewport={{ once: true, margin: '-50px' }} className="flex items-start gap-4 p-6 bg-[#F7FAFC] border-l-2 border-[#3FC9E8]/40 hover:border-[#3FC9E8] transition-colors" data-testid={`problem-bullet-${idx + 1}`}>
                <XCircle className="text-[#5A6B7E]/60 flex-shrink-0 mt-0.5" size={18} />
                <p className="text-[#0F2A4A]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{bullet}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What X Really Does/Is */}
      <section className="relative bg-[#F7FAFC] px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#0F2A4A 1.5px, transparent 1.5px)', backgroundSize: '24px 24px' }} />
        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.truth.label}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.truth.headline}</h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-lg text-[#5A6B7E] leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.truth.intro}</p>
            </div>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
            {data.truth.bullets.map((bullet, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.08 }} viewport={{ once: true, margin: '-50px' }} whileHover={{ y: -4 }} className="bg-white p-6 border border-[#5A6B7E]/15 group" data-testid={`truth-bullet-${idx + 1}`}>
                <div className="w-10 h-10 rounded-full bg-[#3FC9E8]/10 border border-[#3FC9E8]/30 flex items-center justify-center mb-4 group-hover:bg-[#3FC9E8] transition-colors">
                  <CheckCircle2 className="text-[#3FC9E8] group-hover:text-white transition-colors" size={18} />
                </div>
                <p className="text-[#0F2A4A] font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{bullet}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="relative bg-white px-6 md:px-12 lg:px-24 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.whatYouGet.label}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.whatYouGet.headline}</h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-lg text-[#5A6B7E] leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.whatYouGet.intro}</p>
            </div>
          </motion.div>
          <div className="bg-[#0F2A4A] p-8 lg:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-1 bg-gradient-to-l from-[#3FC9E8] to-transparent" />
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
              {data.whatYouGet.bullets.map((bullet, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: idx * 0.08 }} viewport={{ once: true }} className="flex items-start gap-4" data-testid={`get-bullet-${idx + 1}`}>
                  <span className="text-[#3FC9E8] font-bold text-xl flex-shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>0{idx + 1}</span>
                  <p className="text-white/90" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{bullet}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative bg-[#F7FAFC] px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#0F2A4A 1px, transparent 1px), linear-gradient(90deg, #0F2A4A 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-16 max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#3FC9E8]" />
              <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>How It Works</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Four steps. <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>One clear path forward.</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-[#3FC9E8] via-[#1A9FE0] to-[#0A4DA6] opacity-30" />
            {data.process.map((step, idx) => {
              const colors = ['#3FC9E8', '#1A9FE0', '#0A4DA6', '#0F2A4A'];
              return (
                <motion.div key={step.num} data-testid={`process-step-${idx + 1}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.12 }} viewport={{ once: true, margin: '-80px' }} className="relative">
                  <div className="relative z-10 mb-6">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg" style={{ backgroundColor: colors[idx], fontFamily: 'Outfit, sans-serif' }}>{step.num}</div>
                  </div>
                  <h3 className="text-xl font-bold text-[#0F2A4A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                  <p className="text-[#5A6B7E] leading-relaxed text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{step.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who This Is For vs Not For */}
      <section className="bg-white px-6 md:px-12 lg:px-24 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-16 max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#3FC9E8]" />
              <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Right Fit</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Is this <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>built for you?</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true, margin: '-80px' }} className="bg-[#3FC9E8] p-8 lg:p-10 relative" data-testid="for-you-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#0F2A4A] flex items-center justify-center">
                  <CheckCircle2 className="text-[#3FC9E8]" size={20} />
                </div>
                <h3 className="text-2xl font-bold text-[#0F2A4A]" style={{ fontFamily: 'Outfit, sans-serif' }}>This is for you if</h3>
              </div>
              <ul className="space-y-4">
                {data.forYou.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-[#0F2A4A] font-bold mt-1">+</span>
                    <span className="text-[#0F2A4A] font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true, margin: '-80px' }} className="bg-[#0F2A4A] text-white p-8 lg:p-10 relative" data-testid="not-for-you-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                  <XCircle className="text-white/60" size={20} />
                </div>
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>This is not for you if</h3>
              </div>
              <ul className="space-y-4 mb-6">
                {data.notForYou.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-white/40 font-bold mt-1">−</span>
                    <span className="text-white/80" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-[#3FC9E8] italic pt-4 border-t border-white/10" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.notForYouFooter}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why The B2B Digital */}
      <section className="relative bg-[#0F2A4A] text-white px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute top-20 -left-32 w-96 h-96 bg-[#3FC9E8]/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#3FC9E8]" />
                <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#3FC9E8]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Why The B2B Digital</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.why.headline}</h2>
              <p className="text-lg text-white/80 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.why.intro}</p>
            </div>
          </motion.div>
          <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-4">
            {data.why.bullets.map((bullet, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.08 }} viewport={{ once: true }} className="bg-white/[0.04] border border-white/10 p-6 hover:border-[#3FC9E8]/40 transition-colors" data-testid={`why-bullet-${idx + 1}`}>
                <div className="w-8 h-8 rounded-full bg-[#3FC9E8]/15 flex items-center justify-center mb-3">
                  <CheckCircle2 className="text-[#3FC9E8]" size={16} />
                </div>
                <p className="text-white font-medium text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{bullet}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="relative bg-white px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#0F2A4A 1.5px, transparent 1.5px)', backgroundSize: '24px 24px' }} />
        <div className="absolute top-40 -left-20 w-80 h-80 bg-[#3FC9E8]/8 blur-[100px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-32">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-px bg-[#3FC9E8]" />
                    <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#1A9FE0]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>FAQ</span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Questions, <span style={{ background: 'linear-gradient(135deg, #3FC9E8 0%, #1A9FE0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>answered honestly.</span>
                  </h2>
                  <p className="text-lg text-[#5A6B7E] leading-relaxed mb-10" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>The most common questions we get on discovery calls about {data.title}.</p>
                  <div className="bg-[#0F2A4A] text-white p-6 lg:p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-1 bg-[#3FC9E8]" />
                      <div className="absolute top-0 right-0 w-1 h-32 bg-[#3FC9E8]" />
                    </div>
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-10 h-10 rounded-full bg-[#3FC9E8]/15 border border-[#3FC9E8]/30 flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="text-[#3FC9E8]" size={18} />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-[#3FC9E8] font-bold mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Still curious?</p>
                        <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Ask us anything.</h3>
                      </div>
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>We&apos;d rather have a 15-minute honest conversation than write 1,000 words you skim.</p>
                    <Link to="/contact" data-testid="faq-contact-cta" className="inline-flex items-center text-sm font-bold text-[#3FC9E8] hover:gap-3 gap-2 transition-all group" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Book a call <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </motion.div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="space-y-3">
                {data.faqs.map((faq, idx) => {
                  const isOpen = openFaq === idx;
                  const num = String(idx + 1).padStart(2, '0');
                  return (
                    <motion.div key={idx} data-testid={`faq-item-${idx + 1}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.06 }} viewport={{ once: true, margin: '-50px' }} className={`relative border transition-all duration-300 ${isOpen ? 'border-[#3FC9E8] bg-[#F7FAFC]' : 'border-[#5A6B7E]/15 bg-white hover:border-[#1A9FE0]/30'}`}>
                      {isOpen && <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: 'linear-gradient(to bottom, #3FC9E8, #1A9FE0)' }} />}
                      <button onClick={() => setOpenFaq(isOpen ? -1 : idx)} data-testid={`faq-toggle-${idx + 1}`} className="w-full flex items-start gap-5 lg:gap-6 px-6 lg:px-8 py-6 lg:py-7 text-left group">
                        <div className={`flex-shrink-0 transition-all duration-300 ${isOpen ? 'text-[#3FC9E8]' : 'text-[#5A6B7E]/50 group-hover:text-[#1A9FE0]'}`}>
                          <span className="text-sm font-bold tracking-widest" style={{ fontFamily: 'Outfit, sans-serif' }}>Q{num}</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg lg:text-xl font-bold text-[#0F2A4A] leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{faq.q}</h3>
                        </div>
                        <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#3FC9E8] text-[#0F2A4A]' : 'bg-[#F7FAFC] text-[#5A6B7E] group-hover:bg-[#0A4DA6] group-hover:text-white'}`}>
                          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                        </div>
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }} className="overflow-hidden">
                            <div className="px-6 lg:px-8 pb-7 pl-[3.75rem] lg:pl-[5rem]">
                              <p className="text-[#5A6B7E] leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{faq.a}</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
              <div className="mt-10 flex items-center gap-3 text-sm text-[#5A6B7E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <HelpCircle size={16} className="text-[#3FC9E8]" />
                <span>Can&apos;t find your question? <Link to="/contact" className="text-[#0A4DA6] font-medium hover:underline">Send it our way</Link>.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative bg-[#3FC9E8] px-6 md:px-12 lg:px-24 py-20 lg:py-32 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0F2A4A] font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.cta.headline}</h2>
            <p className="text-lg text-[#0F2A4A]/80 leading-relaxed mb-10 max-w-2xl mx-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{data.cta.body}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/contact" data-testid="final-cta-primary" className="inline-flex items-center justify-center bg-[#0F2A4A] text-white font-medium px-8 py-4 hover:bg-[#0A4DA6] transition-all duration-300 active:scale-95">
                Book a FREE Strategy Call <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link to="/contact?scorecard=true" data-testid="final-cta-secondary" className="inline-flex items-center justify-center border-2 border-[#0F2A4A] text-[#0F2A4A] bg-transparent hover:bg-[#0F2A4A] hover:text-white font-medium px-8 py-4 transition-all duration-300 active:scale-95">
                Get The Brand Clarity Scorecard
              </Link>
            </div>
            <a href="mailto:hello@theb2bdigital.com" className="inline-flex items-center gap-2 text-[#0F2A4A] font-medium hover:underline" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <Mail size={18} /> hello@theb2bdigital.com
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
